import React from 'react';
import KPISkeleton from './KPISkeleton';
import TableSkeleton from './TableSkeleton';
import LineChartSkeleton from './LineChartSkeleton';
import BarChartSkeleton from './BarChartSkeleton';
import PieChartSkeleton from './PieChartSkeleton';

interface ChartSkeletonProps {
  /** The chart type determines which skeleton variant to render. */
  type: string;
  /** Optional real title from the plan preview — shown above the skeleton. */
  title?: string;
  /**
   * When true, the chart has been validated by the backend.
   * The skeleton stops animating and shows a confirmed state
   * (checkmark icon, static background) instead of shimmer.
   */
  isReady?: boolean;
}

/**
 * Router component — picks the correct animated skeleton variant based on chart type.
 *
 * Each type has its own unique animation:
 * - kpi → slow shimmer
 * - line/area → SVG stroke-dashoffset draw
 * - bar/histogram → scaleY bounce
 * - pie/donut/funnel/gauge → rotating segments
 * - table/cohort/heatmap → scanning cell highlight
 *
 * When `isReady` is true, the skeleton transitions to a confirmed state:
 * animations stop, a subtle checkmark appears, and the card shows a
 * "ready" visual treatment.
 *
 * When a `title` is provided (from plan preview), it replaces the
 * shimmer title placeholder with the real chart title.
 */
const ChartSkeleton: React.FC<ChartSkeletonProps> = ({ type, title, isReady = false }) => {
  const skeleton = (() => {
    switch (type) {
      case 'kpi':
      case 'kpi_wide':
        return <KPISkeleton title={title} />;

      case 'line':
      case 'area':
      case 'scatter':
        return <LineChartSkeleton title={title} />;

      case 'bar':
      case 'histogram':
      case 'box_plot':
        return <BarChartSkeleton title={title} />;

      case 'pie':
      case 'donut':
      case 'funnel':
      case 'gauge':
        return <PieChartSkeleton title={title} />;

      case 'table':
      case 'cohort':
      case 'heatmap':
        return <TableSkeleton title={title} />;

      default:
        return <BarChartSkeleton title={title} />;
    }
  })();

  if (isReady) {
    return (
      <div className="skeleton-chart-confirmed skeleton-chart-confirmed--animated">
        <div className="skeleton-chart-confirmed__check">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
            <path d="M6 10l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="skeleton-chart-confirmed__content skeleton-chart-confirmed__content--animated">
          {skeleton}
        </div>
      </div>
    );
  }

  return skeleton;
};

export default React.memo(ChartSkeleton);
